/** Number/date wording for the dashboard (pure; unit-tested in test/dash.spec.ts). */
import type { DashPeriod, Kpi } from '../api/dash/types'

const TZ = 'Asia/Bangkok'
const nf = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })
const dayNum = new Intl.DateTimeFormat('th-TH', { day: 'numeric', timeZone: TZ })
const dayMon = new Intl.DateTimeFormat('th-TH', { day: 'numeric', month: 'short', timeZone: TZ })
const monKey = new Intl.DateTimeFormat('en-CA', { year: 'numeric', month: '2-digit', timeZone: TZ })
const dayKey = new Intl.DateTimeFormat('en-CA', { timeZone: TZ })

export const count = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : nf.format(Math.round(n)))
export const money = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : `฿${nf.format(Math.round(n))}`)
export const pct = (n: number | null | undefined): string => (n === null || n === undefined ? '—' : `${n.toFixed(1)}%`)

/** Table form of a duration: 1:48 (min:sec) · 12:05 · 1:02:05 */
export function clock(sec: number | null | undefined): string {
  if (sec === null || sec === undefined) return '—'
  const s = Math.round(sec)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const r = String(s % 60).padStart(2, '0')
  return h ? `${h}:${String(m).padStart(2, '0')}:${r}` : `${m}:${r}`
}

/** Words: 48 วิ · 1 นาที 48 วิ · 2 ชม. 5 นาที */
export function duration(sec: number | null | undefined): string {
  if (sec === null || sec === undefined) return '—'
  const s = Math.round(sec)
  if (s < 60) return `${s} วิ`
  if (s < 3600) return s % 60 ? `${Math.floor(s / 60)} นาที ${s % 60} วิ` : `${s / 60} นาที`
  const m = Math.floor((s % 3600) / 60)
  return m ? `${Math.floor(s / 3600)} ชม. ${m} นาที` : `${Math.floor(s / 3600)} ชม.`
}

export interface Part {
  t: string
  unit: boolean
}

/** The big number of a KPI card split into value / unit parts (units are drawn smaller). */
export function kpiParts(k: Pick<Kpi, 'value' | 'unit'>): Part[] {
  const v = k.value
  if (v === null || v === undefined) return [{ t: '—', unit: false }]
  if (k.unit === 'baht') return [{ t: money(v), unit: false }]
  if (k.unit === 'pct') return [{ t: v.toFixed(1), unit: false }, { t: '%', unit: true }]
  if (k.unit === 'ratio') return [{ t: v.toFixed(2), unit: false }, { t: 'เท่า', unit: true }]
  if (k.unit === 'sec') {
    const s = Math.round(v)
    if (s < 60) return [{ t: String(s), unit: false }, { t: 'วิ', unit: true }]
    if (s < 3600) {
      const out: Part[] = [{ t: String(Math.floor(s / 60)), unit: false }, { t: 'นาที', unit: true }]
      if (s % 60) out.push({ t: String(s % 60), unit: false }, { t: 'วิ', unit: true })
      return out
    }
    return [{ t: String(Math.floor(s / 3600)), unit: false }, { t: 'ชม.', unit: true }, { t: String(Math.floor((s % 3600) / 60)), unit: false }, { t: 'นาที', unit: true }]
  }
  return [{ t: count(v), unit: false }]
}

export type Tone = 'good' | 'bad' | 'flat'

/** "+12%" / "+0.5 จุด" / "ช้าลง 22 วิ" with good/bad colour by the KPI's better direction. null = nothing to compare. */
export function delta(k: Pick<Kpi, 'value' | 'prev' | 'unit' | 'better'>): { text: string; tone: Tone; dir: 'up' | 'down' | null } | null {
  const { value: v, prev: p } = k
  if (v === null || v === undefined || p === null || p === undefined) return null
  const diff = v - p
  const dir = diff > 0 ? 'up' : diff < 0 ? 'down' : null
  const tone: Tone = dir === null ? 'flat' : dir === k.better ? 'good' : 'bad'
  if (k.unit === 'sec') {
    const s = Math.abs(Math.round(diff))
    if (!s) return { text: 'เท่าเดิม', tone: 'flat', dir: null }
    return { text: `${diff > 0 ? 'ช้าลง' : 'เร็วขึ้น'} ${duration(s)}`, tone, dir }
  }
  if (k.unit === 'pct') {
    const d = Math.round(diff * 10) / 10
    return { text: d === 0 ? 'เท่าเดิม' : `${d > 0 ? '+' : ''}${d.toFixed(1)} จุด`, tone: d === 0 ? 'flat' : tone, dir }
  }
  if (p === 0) return v === 0 ? { text: 'เท่าเดิม', tone: 'flat', dir: null } : { text: `+${k.unit === 'ratio' ? v.toFixed(2) : count(v)}`, tone, dir }
  const r = Math.round((diff / p) * 100)
  return { text: r === 0 ? 'เท่าเดิม' : `${r > 0 ? '+' : ''}${r}%`, tone: r === 0 ? 'flat' : tone, dir }
}

/** 19–25 ก.ย. · 28 ส.ค.–3 ก.ย. · 26 ก.ย. (the end of the period is exclusive) */
export function dateRange(fromIso: string, toIso: string): string {
  const a = new Date(fromIso)
  const b = new Date(Date.parse(toIso) - 1)
  if (dayKey.format(a) === dayKey.format(b)) return dayMon.format(a)
  if (monKey.format(a) === monKey.format(b)) return `${dayNum.format(a)}–${dayMon.format(b)}`
  return `${dayMon.format(a)}–${dayMon.format(b)}`
}

/** 7 วันล่าสุด · 7 วันก่อนหน้า · 7 วัน (3 ช่วงก่อน) · วันนี้ · เมื่อวาน · กำหนดเอง 14 วัน */
export function periodLabel(p: Pick<DashPeriod, 'range' | 'offset' | 'days'>): string {
  if (p.range === 'custom') return p.days === 1 ? 'กำหนดเอง 1 วัน' : `กำหนดเอง ${p.days} วัน`
  if (p.range === 'today') return p.offset === 0 ? 'วันนี้' : p.offset === 1 ? 'เมื่อวาน' : `${p.offset} วันก่อน`
  if (p.offset === 0) return `${p.days} วันล่าสุด`
  if (p.offset === 1) return `${p.days} วันก่อนหน้า`
  return `${p.days} วัน · ${p.offset} ช่วงก่อน`
}

/** Under each KPI: what the delta compares with. */
export function vsLabel(p: Pick<DashPeriod, 'range' | 'days' | 'running'>): string {
  if (p.range === 'custom') return `เทียบ ${p.days} วันก่อนหน้า`
  if (p.range === 'today') return p.running ? 'เทียบเมื่อวานช่วงเวลาเดียวกัน' : 'เทียบวันก่อนหน้า'
  return `เทียบ ${p.days} วันก่อน`
}

/** Width for a bar (0–100 %) against the largest value of the column. */
export const barWidth = (v: number | null | undefined, max: number): string =>
  `${max > 0 && v ? Math.max(2, Math.min(100, (v / max) * 100)).toFixed(1) : 0}%`

export const RANGE_OPTIONS = [
  { v: 'today', label: 'วันนี้' },
  { v: '7d', label: '7 วัน' },
  { v: '30d', label: '30 วัน' },
  { v: '90d', label: '90 วัน' },
  { v: 'custom', label: 'กำหนดเอง' },
] as const

// ---------------------------------------------------------------- "กำหนดเอง" (Bangkok calendar days)

const DAY_MS = 86400000
const BKK_MS = 7 * 3600000
/** At most this many days (the server says the same: api/app/dashboard.py MAX_CUSTOM_DAYS). */
export const MAX_CUSTOM_DAYS = 92
const RANGE_DAYS: Record<Exclude<DashPeriod['range'], 'custom'>, number> = { today: 1, '7d': 7, '30d': 30, '90d': 90 }

/** YYYY-MM-DD of an instant in Bangkok. */
export function bkkDay(t: number | Date): string {
  return new Date((typeof t === 'number' ? t : t.getTime()) + BKK_MS).toISOString().slice(0, 10)
}

/** Midnight (Bangkok) of a YYYY-MM-DD, as epoch ms. */
export function dayStart(day: string): number {
  return Date.parse(`${day}T00:00:00+07:00`)
}

export function addDays(day: string, n: number): string {
  return bkkDay(dayStart(day) + n * DAY_MS + 12 * 3600000)
}

/** Days from `a` to `b`, both included (a = b → 1). */
export function daySpan(a: string, b: string): number {
  return Math.round((dayStart(b) - dayStart(a)) / DAY_MS) + 1
}

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/

/** Why a chosen range cannot be used (Thai), or null. `today` = Bangkok today. */
export function customProblem(from: string | null | undefined, to: string | null | undefined, today: string): string | null {
  if (!from || !to || !DAY_RE.test(from) || !DAY_RE.test(to)) return 'เลือกวันเริ่มและวันสิ้นสุด'
  if (from > to) return 'วันเริ่มต้องไม่หลังวันสิ้นสุด'
  if (from > today) return 'เลือกวันที่ผ่านมาแล้วหรือวันนี้'
  if (daySpan(from, to > today ? today : to) > MAX_CUSTOM_DAYS) return `เลือกได้ไม่เกิน ${MAX_CUSTOM_DAYS} วัน`
  return null
}

/** The arrows on a custom range: the same number of days before (-1) or after (+1); never past today. */
export function shiftCustom(from: string, to: string, dir: -1 | 1, today: string): { from: string; to: string } {
  const n = daySpan(from, to)
  let a = addDays(from, dir * n)
  let b = addDays(to, dir * n)
  if (b > today) {
    b = today
    a = addDays(today, -(n - 1))
  }
  return { from: a, to: b }
}

/**
 * The period the server answers for a query — the same rules as api/app/dashboard.py (Bangkok days; a running
 * period ends now; the previous one is the same length right before, cut at the same point). Mocks + labels.
 */
export function periodOf(q: { range: DashPeriod['range']; offset: number; from?: string | null; to?: string | null }, now: number): DashPeriod {
  if (q.range === 'custom' && q.from && q.to) {
    const today = bkkDay(now)
    const last = q.to > today ? today : q.to
    const days = daySpan(q.from, last)
    const from = dayStart(q.from)
    const to = Math.min(dayStart(last) + DAY_MS, now)
    return {
      from: new Date(from).toISOString(), to: new Date(to).toISOString(), days, range: 'custom', offset: 0,
      prev_from: new Date(from - days * DAY_MS).toISOString(), prev_to: new Date(to - days * DAY_MS).toISOString(),
      running: to === now, first_day: q.from, last_day: last,
    }
  }
  const range = q.range === 'custom' ? '7d' : q.range
  const days = RANGE_DAYS[range]
  const end = dayStart(bkkDay(now)) + DAY_MS - q.offset * days * DAY_MS
  const to = Math.min(end, now)
  const from = end - days * DAY_MS
  return {
    from: new Date(from).toISOString(), to: new Date(to).toISOString(), days, range, offset: q.offset,
    prev_from: new Date(from - days * DAY_MS).toISOString(), prev_to: new Date(to - days * DAY_MS).toISOString(), running: to === now,
  }
}

export const CHANNEL_OPTIONS = [
  { v: 'all', label: 'ทุกช่องทาง' },
  { v: 'fb', label: 'Facebook' },
  { v: 'ig', label: 'Instagram' },
  { v: 'line', label: 'LINE' },
  { v: 'comment', label: 'คอมเมนต์' },
  { v: 'web', label: 'แชทเว็บ' },
] as const

/** An ad without a name yet: "แอด …0921" / "ลิงก์ pack7-sep". */
export function adName(a: { label: string | null; ad_id: string | null; ref: string | null; source: string | null }): string {
  if (a.label) return a.label
  if (a.ad_id) return `แอดรหัส …${a.ad_id.slice(-6)}`
  if (a.ref) return `ลิงก์ติดรหัส ${a.ref}`
  return a.source === 'SHORTLINK' ? 'ลิงก์ m.me' : 'แอด (ไม่มีรหัส)'
}
